/**
 * A dropdown whose vocabulary the user manages in place: each entry selects on click and carries a
 * pencil (rename) and a minus (remove); the footer's free-text row adds a new entry through a small
 * modal. Rendered in a portal like the row menu, so clipping containers never crop it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';

export interface ManagedOption {
  id: string;
  label: string;
  uses?: number;
}

const POP_GAP_PX = 4;
const VIEWPORT_PAD_PX = 8;

export function ManagedSelect({ value, placeholder, options, disabled, onSelect, onAdd, onRename, onRemove, ariaLabel }: {
  /** Null until something is chosen; the button then shows the placeholder. */
  value: string | null;
  placeholder?: string;
  options: ManagedOption[];
  disabled?: boolean;
  onSelect: (label: string) => void;
  onAdd: (label: string) => void;
  onRename: (id: string, label: string) => void;
  onRemove: (id: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; id?: string; text: string } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const btn = btnRef.current;
      const pop = popRef.current;
      if (!btn || !pop) return;
      const r = btn.getBoundingClientRect();
      const below = r.bottom + POP_GAP_PX + pop.offsetHeight <= window.innerHeight - VIEWPORT_PAD_PX;
      const top = below ? r.bottom + POP_GAP_PX : Math.max(VIEWPORT_PAD_PX, r.top - POP_GAP_PX - pop.offsetHeight);
      pop.style.setProperty('--pop-x', `${Math.round(Math.max(VIEWPORT_PAD_PX, Math.min(r.left, window.innerWidth - pop.offsetWidth - VIEWPORT_PAD_PX)))}px`);
      pop.style.setProperty('--pop-y', `${Math.round(top)}px`);
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const closeOnScroll = () => setOpen(false);
    window.addEventListener('pointerdown', close);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [open]);

  const submitModal = () => {
    if (!modal) return;
    const text = modal.text.trim();
    if (!text) return;
    if (modal.mode === 'add') onAdd(text);
    else if (modal.id) onRename(modal.id, text);
    setModal(null);
  };

  return (
    <>
      <button ref={btnRef} type="button" className={`managed-btn${open ? ' on' : ''}`} disabled={disabled}
        aria-label={ariaLabel} aria-expanded={open} onClick={() => setOpen(!open)}>
        <b className={value === null ? 'managed-empty' : undefined}>{value ?? placeholder}</b>
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
      </button>
      {open && createPortal(
        <div className="row-menu-pop managed-pop" role="menu" ref={popRef}>
          {options.map((o) => (
            <div key={o.id} className={`managed-row${o.label === value ? ' on' : ''}`}>
              <button type="button" role="menuitem" className="managed-pick" onClick={() => { onSelect(o.label); setOpen(false); }}>
                {o.label}{o.uses !== undefined && o.uses > 0 && <span className="sub managed-uses">{o.uses}</span>}
              </button>
              <button type="button" className="managed-tool managed-edit" title={`Rename ${o.label}`} aria-label={`Rename ${o.label}`}
                onClick={() => { setModal({ mode: 'edit', id: o.id, text: o.label }); setOpen(false); }}>
                <Icon name="pencil" />
              </button>
              <button type="button" className="managed-tool danger" title={`Remove ${o.label}`} aria-label={`Remove ${o.label}`}
                onClick={() => { onRemove(o.id); setOpen(false); }}>
                <Icon name="minus" />
              </button>
            </div>
          ))}
          <div className="row-menu-hr" />
          <button type="button" className="managed-add" onClick={() => { setModal({ mode: 'add', text: '' }); setOpen(false); }}>
            <Icon name="plus" /> Add an entry…
          </button>
        </div>,
        document.body,
      )}
      {modal && createPortal(
        <>
          <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setModal(null)} />
          <div className="modal managed-modal" role="dialog" aria-label={modal.mode === 'add' ? 'Add an entry' : 'Rename entry'}>
            <h2>{modal.mode === 'add' ? 'Add an entry' : 'Rename entry'}</h2>
            <label className="field">
              <input autoFocus value={modal.text} placeholder="Name" aria-label="Entry name" maxLength={40}
                onChange={(e) => setModal({ ...modal, text: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') submitModal(); if (e.key === 'Escape') setModal(null); }} />
            </label>
            {modal.mode === 'edit' && <span className="sub">Every clip carrying the old name follows the rename.</span>}
            <div className="row between">
              <button type="button" className="btn quiet" onClick={() => setModal(null)}>Cancel</button>
              <button type="button" className="btn primary" disabled={!modal.text.trim()} onClick={submitModal}>
                {modal.mode === 'add' ? 'Add' : 'Rename'}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
